import { MEALS, dayFullKey, dowToDi } from './constants';
import { MI_BY_MEAL_TYPE } from './slotMeals';
import { isEntryMeal, isRecipeMeal } from '../../../redux/mealEntry';
import { SHELF_LIFE_TABLE } from '../../../data/shelfLife';
import type { PlannedMeal } from '../../../redux/mealPlanSlice';
import type { InventoryItem } from '../../../redux/inventorySlice';
import type { PantryExpiryDate } from '../../../utils/pantryExpiry';
import { LEFTOVER_CATEGORY, isCookedLeftover } from '../../../utils/pantryLocation';
import { addDays } from '../../../utils/localDate';

/**
 * How long leftovers keep, read from the shelf-life table rather than restated
 * here. `shelfLife.ts` already carries the row the pantry uses for the same
 * food, and it is covered by the shelf-life eval — a second copy of "4" in this
 * file is a second thing to keep true.
 */
const LEFTOVER_ROW = SHELF_LIFE_TABLE.find((e) => e.key === 'leftovers');
export const FRIDGE_LEFTOVER_DAYS = LEFTOVER_ROW?.days?.fridge ?? 4;
export const FREEZER_LEFTOVER_DAYS = LEFTOVER_ROW?.days?.freezer ?? 90;

/** The pantry zone a frozen leftover is filed under; the category is shared. */
export const FREEZER_LOCATION = 'freezer';
export { LEFTOVER_CATEGORY };

export interface LeftoverCandidate {
  /** Stable React key, also what dedupe is keyed on. */
  key: string;
  kind: 'fridge' | 'freezer';
  refRecipeId: string;
  /**
   * Portions still available. `undefined` means nobody was ever asked — a meal
   * cooked before this feature shipped. Those stay offerable (hiding them would
   * make the picker look broken for the whole existing history) but are never
   * counted or decremented.
   */
  remaining?: number;

  // fridge only
  sourceMealId?: string;
  cookedOn?: string;      // YYYY-MM-DD
  mealLabelKey?: string;

  // freezer only
  pantryItemId?: string;
  title?: string;         // frozen rows carry their own name; no recipe to resolve
  expiresOn?: string;     // YYYY-MM-DD
}

/** Minimal shape of i18next's `t`, so this stays a plain module. */
type Translate = (key: string, opts?: Record<string, unknown>) => string;

/**
 * Shift a YYYY-MM-DD date by `n` days.
 *
 * Kept as a named re-export rather than an alias at the call sites: "shift this
 * leftover's date" is the vocabulary this module speaks, and the leftover
 * window is the thing the name protects.
 */
export const shiftDate = addDays;

/**
 * The date range a caller must load to build fridge candidates for `targetDate`.
 *
 * Wider than the lookback on purpose: portions already placed on days *after*
 * the target still consume from the same cook, so they have to be visible or
 * the remaining count reads high.
 */
export const leftoverPoolRange = (targetDate: string) => ({
  start: shiftDate(targetDate, -FRIDGE_LEFTOVER_DAYS),
  end: shiftDate(targetDate, FRIDGE_LEFTOVER_DAYS),
});

/** `PantryExpiryDate` is calendar fields, not a string. Render it as YYYY-MM-DD. */
export function expiryToIso(e: PantryExpiryDate | undefined): string | undefined {
  if (!e) return undefined;
  const month = String(e.month).padStart(2, '0');
  const day = String(e.day).padStart(2, '0');
  return `${e.year}-${month}-${day}`;
}

/** The day a meal was actually cooked, falling back to the day it was planned. */
export const cookDateOf = (m: PlannedMeal): string =>
  m.cookedAt ? m.cookedAt.slice(0, 10) : m.scheduledFor;

/** Every leftover entry in `meals` that draws on `sourceMealId`. */
function portionsPlacedAgainst(meals: PlannedMeal[], sourceMealId: string): number {
  return meals.reduce((sum, m) => {
    if (!isEntryMeal(m) || m.entry?.type !== 'leftover') return sum;
    if (m.entry.sourceMealId !== sourceMealId) return sum;
    return sum + (m.entry.portions ?? 1);
  }, 0);
}

/**
 * Cooked meals whose leftovers could plausibly still be in the fridge on
 * `targetDate`.
 *
 * `meals` should span `leftoverPoolRange(targetDate)` — pass it straight from
 * `selectMealsForDateRange`. The old version walked the displayed week's slot
 * grid by day *index*, which is why a Sunday cook could never reach a Tuesday
 * lunch: Sunday is index 6 and Tuesday is index 1, so it sorted as "later" and
 * fell outside the loop. Dates do not have that problem.
 */
export function buildFridgeCandidates(
  meals: PlannedMeal[],
  targetDate: string,
): LeftoverCandidate[] {
  const earliest = shiftDate(targetDate, -FRIDGE_LEFTOVER_DAYS);
  const out: LeftoverCandidate[] = [];

  for (const m of meals) {
    if (!isRecipeMeal(m)) continue;
    if (m.status !== 'cooked') continue;

    const cookedOn = cookDateOf(m);
    // Cooked after the slot it would feed: the food does not exist yet.
    if (cookedOn > targetDate) continue;
    // Older than the fridge keeps it.
    if (cookedOn < earliest) continue;

    const declared = m.leftoverFridgePortions;
    const remaining =
      declared === undefined
        ? undefined
        : Math.max(0, declared - portionsPlacedAgainst(meals, m.id));

    out.push({
      key: `fridge:${m.id}`,
      kind: 'fridge',
      refRecipeId: m.recipeId,
      sourceMealId: m.id,
      cookedOn,
      mealLabelKey: MEALS[MI_BY_MEAL_TYPE[m.mealType] ?? 1].labelKey,
      remaining,
    });
  }

  // One row per cooked meal already (keyed on meal id), most recent first.
  return out.sort((a, b) => (b.cookedOn ?? '').localeCompare(a.cookedOn ?? ''));
}

/**
 * Frozen leftovers, straight from the pantry.
 *
 * No date window: freezing is precisely how you escape the fridge's four days,
 * so a frozen portion stays offerable until its own expiry.
 */
export function buildFreezerCandidates(items: InventoryItem[]): LeftoverCandidate[] {
  return items
    // `source`, not the category: a shop-bought "Repas préparé Kirkland" is
    // filed under Leftovers too, and it was never cooked here — the rule is
    // that nothing is offered as leftovers unless the user cooked it.
    .filter(isCookedLeftover)
    .filter((i) => i.location === FREEZER_LOCATION)
    .filter((i) => !i.status || i.status === 'active')
    .filter((i) => (i.quantity ?? 0) > 0)
    .map((i) => ({
      key: `freezer:${i.id}`,
      kind: 'freezer' as const,
      refRecipeId: i.leftoverRecipeId ?? '',
      pantryItemId: i.id,
      title: i.name,
      remaining: i.quantity,
      expiresOn: expiryToIso(i.expiryDate),
    }))
    .sort((a, b) => (a.expiresOn ?? '9999').localeCompare(b.expiresOn ?? '9999'));
}

/** A candidate with nothing left to serve. Rendered greyed, never hidden. */
export const isExhausted = (c: LeftoverCandidate): boolean => c.remaining === 0;

/** Days before an expiry at which the pill turns amber instead of sage. */
export const EXPIRY_WARN_DAYS = 7;

export interface LeftoverRowMeta {
  title: string;
  sub: string;
  /**
   * Absent when there is no count to show. The badge answers "how many are
   * left"; when we do not know, showing nothing is the honest answer. Labelling
   * the gap ("non compté") described our own bookkeeping rather than the food,
   * and sat where every other row shows a number, so it read like a count that
   * had failed to load.
   */
  badge?: string;
  /** `muted` also means unpickable. */
  tone: 'count' | 'warn' | 'muted';
  disabled: boolean;
}

/**
 * Everything the two pickers draw for one candidate.
 *
 * Desktop renders a modal and mobile a sheet, with different styles but the
 * same rules; keeping the rules here is what stops the two surfaces disagreeing
 * about what "épuisé" means.
 */
export function leftoverRowMeta(
  c: LeftoverCandidate,
  t: Translate,
  resolveTitle: (recipeId: string) => string | undefined,
  formatDate: (isoDate: string) => string,
  today: string,
): LeftoverRowMeta {
  const title =
    c.kind === 'freezer'
      ? (c.title ?? t('entry.leftovers'))
      : (resolveTitle(c.refRecipeId) ?? t('entry.subRecentMeal'));

  let sub: string;
  if (c.kind === 'freezer') {
    sub = c.expiresOn
      ? t('thisWeek.leftoverExpiresOn', { date: formatDate(c.expiresOn) })
      : t('thisWeek.leftoverFromFreezer');
  } else {
    const di = dowToDi(new Date((c.cookedOn ?? today) + 'T12:00:00').getDay());
    sub = `${t(dayFullKey(di))} ${formatDate(c.cookedOn ?? today)} · ${t(
      c.mealLabelKey ?? MEALS[1].labelKey,
    ).toLowerCase()}`;
  }

  if (c.remaining === undefined) {
    // Cooked before anyone was asked, so there is no count to show. Still
    // pickable — it is real food — just with no badge at all.
    return { title, sub, tone: 'muted', disabled: false };
  }
  if (c.remaining <= 0) {
    return { title, sub, badge: t('thisWeek.leftoverExhausted'), tone: 'muted', disabled: true };
  }

  const badge = t('thisWeek.leftoverPortionsLeft', { count: c.remaining });
  const soon =
    c.expiresOn !== undefined && c.expiresOn <= shiftDate(today, EXPIRY_WARN_DAYS);
  return { title, sub, badge, tone: soon ? 'warn' : 'count', disabled: false };
}

/**
 * Note: the result is persisted onto the meal as `entry.sub`, so it freezes the
 * language that was active when the leftover was created. Making it re-translate
 * would mean storing the parts rather than the sentence — out of scope here.
 */
export const leftoverSubLabel = (c: LeftoverCandidate, t: Translate): string => {
  if (c.kind === 'freezer') return t('thisWeek.leftoverFromFreezer');
  const di = dowToDi(new Date((c.cookedOn ?? '') + 'T12:00:00').getDay());
  return t('thisWeek.leftoverFrom', {
    day: t(dayFullKey(di)).toLowerCase(),
    meal: t(c.mealLabelKey ?? MEALS[1].labelKey).toLowerCase(),
  });
};
